import { useCallback, useEffect, useState } from "react";
import { History, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "./ui";
import { EMPTY_SEL, describeParams, fmtTime } from "./preview/meta";
import { RunForm } from "./preview/RunForm";
import { RunFailed, RunRunning, RunWarnings } from "./preview/RunStatus";
import { RunSummary } from "./preview/RunSummary";
import { RowsPanel } from "./preview/RowsPanel";
import { ApplyBar } from "./preview/ApplyBar";
import { ApplyStatus } from "./preview/ApplyStatus";
import { UndoDialog } from "./preview/UndoDialog";

function RunPicker({ runs, current, onPick }) {
  if (!runs?.length) return null;
  return (
    <label className="flex items-center gap-2 text-xs text-slate-500"><History className="h-3.5 w-3.5" /> Showing
      <select value={current?.id || ""} onChange={(e) => onPick(e.target.value)} data-testid="bsp-run-picker" className="h-8 max-w-md rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700">
        {runs.map((r) => <option key={r.id} value={r.id}>{`${fmtTime(r.started_at)} · ${r.status} · ${describeParams(r.params)}`}</option>)}
      </select></label>
  );
}

export function TrackedItemsPreview() {
  const [runs, setRuns] = useState(null);
  const [canExport, setCanExport] = useState(false);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("add");
  const [sel, setSel] = useState(EMPTY_SEL);
  const [meta, setMeta] = useState({ canApply: false, running: false });
  const [applyId, setApplyId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [undo, setUndo] = useState(null);
  const closeUndo = useCallback(() => setUndo(null), []);
  const onFinished = useCallback(() => { setReloadKey((k) => k + 1); setSel(EMPTY_SEL); }, []);
  const onStarted = (id) => { setApplyId(id); setMeta((m) => ({ ...m, running: true })); setSel(EMPTY_SEL); setReloadKey((k) => k + 1); };
  const show = (r) => { setRun(r); setTab("add"); setSel(EMPTY_SEL); };
  const loadRuns = useCallback((pickLatest = false) => api.get("/billing-sync/preview/runs").then(({ data }) => {
    setRuns(data.runs); setCanExport(data.can_export);
    if (pickLatest) setRun(data.runs[0] || null);
  }), []);
  useEffect(() => { loadRuns(true).catch((e) => { toast.error(errMsg(e, "Could not load the preview")); setRuns([]); }); }, [loadRuns]);
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const t = setInterval(() => api.get(`/billing-sync/preview/runs/${run.id}`).then(({ data }) => {
      setRun(data); if (data.status !== "running") loadRuns();
    }).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [run?.id, run?.status, loadRuns]);
  const start = async (params) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing-sync/preview/runs", params);
      const { data: r } = await api.get(`/billing-sync/preview/runs/${data.id}`);
      show(r); loadRuns();
    } catch (e) { toast.error(errMsg(e, "Could not start the preview")); } finally { setBusy(false); }
  };
  if (!runs) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-slate-300" />;
  return (
    <div className="space-y-4" data-testid="bs-tracked-items-preview">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-2.5 text-xs text-emerald-900" data-testid="bsp-readonly-banner">
        <ShieldCheck className="h-4 w-4 text-emerald-600" /><b>The preview is read-only.</b> It compares the platforms with Stax Bill the way the tracked-items scripts do and lists what they would change. Nothing changes in Stax Bill until you tick rows and confirm Apply.
      </div>
      <RunForm busy={busy || run?.status === "running"} onRun={start} />
      <RunPicker runs={runs} current={run} onPick={(id) => show(runs.find((r) => r.id === id))} />
      {!run && <p className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="bsp-empty">No preview yet. Pick the platforms and press <b>Run preview</b>.</p>}
      {run?.status === "running" && <RunRunning run={run} />}
      {run?.status === "failed" && <RunFailed run={run} />}
      {run?.status === "done" && (<>
        <RunWarnings list={run.warnings} />
        <RunSummary run={run} tab={tab} onTab={setTab} />
        <ApplyStatus run={run} applyId={applyId} onFinished={onFinished} onMeta={setMeta} />
        <RowsPanel run={run} kind={tab} canExport={canExport} sel={sel} onSel={meta.canApply ? setSel : null}
                   onUndo={meta.canApply && !meta.running ? (items) => setUndo({ items }) : null} reloadKey={reloadKey} />
        <ApplyBar run={run} sel={sel} onClear={() => setSel(EMPTY_SEL)} onStarted={onStarted} busy={meta.running} />
        {undo && <UndoDialog target={undo} onClose={closeUndo} onStarted={(id) => { setUndo(null); onStarted(id); }} />}
      </>)}
    </div>
  );
}
